<script lang="ts" setup>
import {
  CellGroup,
  Field,
  Picker,
  PickerOption,
  Popup,
  showLoadingToast,
} from "vant";
import { Gender, Mate } from "@/types/entity";
import { computed, ref } from "vue";
import {
  useAllGender,
  useAllMateType,
  useGender,
  useMateTypeTextByMate,
} from "@/use/common";
import TagSelector from "@/components/mobile/common/TagSelector.vue";
import { MediaUploadFileItem } from "@/types/component";
import { MateType } from "@/enums/mateEnums";
import { useMate } from "@/stores/mate";
import { useWorldview } from "@/stores/worldview";
import router from "@/router";
import { readMateSprite } from "@/apis/script-game";
import { MateSprite } from "@/types/game";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { useDungeon } from "@/stores/dungeon";

const props = defineProps<{
  mateId: number;
  isSaving: boolean;
  isSelfSetting?: boolean;
}>();

const mate = computed(() => {
  return mateStore.mateIdMap.get(props.mateId)!;
});

const activeMateSprite = computed(() => {
  if (!mate.value.spritezz?.length) return undefined;
  let mateSprite = mate.value.spritezz.find((v) => v.isDefault);
  if (!mateSprite) mateSprite = mate.value.spritezz[0];
  return mateSprite;
});

type UpdateKey = keyof Mate | Array<keyof Mate>;
const mateStore = useMate();
const emit = defineEmits<{
  (e: "update:isSaving", isSaving: boolean): void;
  (e: "update:mate", mate: UpdateKey): void;
}>();

const showMateTypePicker = ref(false);
const showGenderPicker = ref(false);
const MateTypeOptions = useAllMateType();
const GenderOptions = useAllGender();
const currentMate = ref(mate.value!);
const worldviewStore = useWorldview();

const typeDisplay = ref(useMateTypeTextByMate(currentMate.value));
const genderDisplay = ref(useGender(currentMate.value.gender));
// Event Handler
const onTypePickerConfirm = ({ selectedOptions }: PickerOption) => {
  typeDisplay.value = selectedOptions[0]?.text;
  currentMate.value.type = selectedOptions[0]?.value as MateType;
  emit("update:mate", "type");
  showMateTypePicker.value = false;
};
const onGenderPickerConfirm = ({ selectedOptions }: PickerOption) => {
  genderDisplay.value = selectedOptions[0]?.text;
  currentMate.value.gender = selectedOptions[0]?.value as Gender;
  emit("update:mate", "gender");
  showGenderPicker.value = false;
};

// TagSelector
const showAvatarUploadPopup = ref(false);

const updateTag = (val: string[]) => {
  currentMate.value.tag = val.join(",");
  emit("update:mate", "tag");
};

// avatar
const onAvatarSelected = (list: MediaUploadFileItem[]) => {
  const { mediaId, url } = list[0];
  currentMate.value.mediaId = mediaId;
  currentMate.value.mediaUrl = url!;
  emit("update:mate", ["mediaId", "mediaUrl"]);
};

const showWorldViewPicker = ref(false);
const worldviewType = ref<"bornPlanet" | "currentPlanet">();
const clickShowWorldViewPicker = (type: "bornPlanet" | "currentPlanet") => {
  worldviewType.value = type;
  showWorldViewPicker.value = true;
};

// const worldviewList = ref<Worldview[]>([]);
const onWorldViewConfirm = (value: number) => {
  showWorldViewPicker.value = false;
  if (worldviewType.value === "bornPlanet") {
    currentMate.value.worldviewId = value;
    currentMate.value.worldview = worldviewStore.worldviewMap[value];
    emit("update:mate", "worldviewId");
  } else {
    currentMate.value.currentWorldviewId = value;
    emit("update:mate", "currentWorldviewId");
  }
};

const richEditorCallback = (content: string, type: keyof Mate) => {
  currentMate.value[type] = content as never;
  emit("update:mate", type);
};

const clickPortrait = async (sprite: MateSprite | undefined) => {
  if (!sprite) {
    router.push({
      name: "mateCreate",
    });
    return;
  }

  const loading = showLoadingToast(`加载中...`);
  const { item } = await readMateSprite(sprite!.uuid!);
  useDungeon().fetchMateSpriteList(item.mateId!, true);
  loading.close();
  router.push({
    name: "mateCreate",
    query: {
      spriteId: item.id,
    },
  });
};

// MBTI 相关
const showMBTIModal = ref(false);
const handleMBTISelect = (mbti: string) => {
  mate.value.mbti = mbti;
  emit("update:mate", "mbti");
};
</script>
<template>
  <div>
    <div class="mb-2 flex flex-col">
      <div class="flex flex-col gap-2 items-center mt-6">
        <div
          :class="[
            'flex justify-center items-center border border-base-content/10  relative overflow-hidden max-w-[20rem]',
            isSelfSetting
              ? 'w-[120px] aspect-[1/1] rounded-full'
              : 'w-[100px] aspect-[3/4] rounded-xl',
          ]"
        >
          <user-avatar
            v-if="mate.mediaUrl"
            :avatar-url="mate.mediaUrl"
            @click="showAvatarUploadPopup = true"
            class="w-full"
          />
          <div
            class="absolute top-0 left-0 w-full h-full flex flex-col gap-2 items-center justify-center bg-base-100/50"
            v-else
          >
            <div class="flex flex-col gap-2">
              <button
                class="btn btn-sm rounded"
                @click="showAvatarUploadPopup = true"
              >
                {{ $t("Buttons.select") }}
              </button>
            </div>
          </div>
        </div>

        <div class="text-base-content/50">
          {{ $t("User.mate.appearance.avatar") }}
        </div>
      </div>
    </div>

    <cell-group inset>
      <!-- 名字 -->
      <field
        :label="$t('User.mate.basicInfo.name')"
        v-model="currentMate.name"
        :placeholder="$t('Message.none')"
        :rules="[
          { required: true, message: $t('User.mate.basicInfo.rules.name') },
        ]"
        @blur="emit('update:mate', 'name')"
        required
      />
      <!-- 类型 -->
      <field
        v-if="!isSelfSetting"
        v-model="typeDisplay"
        :label="$t('User.mate.basicInfo.type')"
        :placeholder="$t('Message.none')"
        :rules="[
          { required: true, message: $t('User.mate.basicInfo.rules.type') },
        ]"
        is-link
        readonly
        required
        @click="showMateTypePicker = true"
      />
      <!-- 世界观 -->
      <field
        v-if="!isSelfSetting"
        :model-value="
          worldviewStore.worldviewMap[Number(mate.worldviewId)]?.name ||
          $t(`Message.default`)
        "
        :label="$t('User.mate.basicInfo.bornPlanet')"
        is-link
        readonly
        @click="clickShowWorldViewPicker('bornPlanet')"
      />
      <field
        v-if="!isSelfSetting"
        :model-value="
          worldviewStore.worldviewMap[Number(mate.currentWorldviewId)]?.name ||
          $t(`Message.default`)
        "
        :label="$t('User.mate.basicInfo.currentPlanet')"
        is-link
        readonly
        @click="clickShowWorldViewPicker('currentPlanet')"
      />
      <!-- mbti -->
      <field
        v-if="!isSelfSetting"
        :model-value="mate.mbti"
        :placeholder="$t('Message.none')"
        label="MBTI 类型"
        is-link
        readonly
        @click="showMBTIModal = true"
      />
      <!-- 用户称呼 -->
      <field
        v-if="!isSelfSetting"
        v-model="currentMate.userAddressName"
        label="对我的称呼"
        :placeholder="'默认: 主人'"
        @blur="emit('update:mate', 'userAddressName')"
      />
      <!-- 与我的关系 -->
      <CustomRichTextCell
        v-if="!isSelfSetting"
        :title="$t('User.mate.relation.myRelation')"
        :module-name="$t('User.mate.basicInfo.title')"
        :content="mate.relationWithMe"
        :cb="(content:string) => richEditorCallback(content, 'relationWithMe')"
      />

      <!-- 性别 -->
      <field
        v-model="genderDisplay"
        :label="$t('User.mate.basicInfo.gender')"
        :placeholder="$t('Message.none')"
        :rules="[
          { required: true, message: $t('User.mate.basicInfo.rules.gender') },
        ]"
        is-link
        readonly
        @click="showGenderPicker = true"
      />

      <!-- 生日 -->
      <field
        :label="$t('User.mate.basicInfo.birthday')"
        v-model="currentMate.birthday"
        :placeholder="$t('Message.none')"
        @blur="emit('update:mate', 'birthday')"
      />

      <!-- 种族 -->
      <CustomRichTextCell
        :title="$t('User.mate.basicInfo.race')"
        :module-name="$t('User.mate.basicInfo.title')"
        :content="mate.race"
        :cb="(content:string) => richEditorCallback(content, 'race')"
      />

      <!-- 职业 -->
      <CustomRichTextCell
        :title="$t('User.mate.basicInfo.occupation')"
        :module-name="$t('User.mate.basicInfo.title')"
        :content="mate.occupation"
        :cb="(content:string) => richEditorCallback(content, 'occupation')"
      />

      <!-- 个人简介 -->
      <CustomRichTextCell
        :title="$t('User.mate.basicInfo.bio')"
        :module-name="$t('User.mate.basicInfo.title')"
        :content="mate.bio"
        :cb="(content:string) => richEditorCallback(content, 'bio')"
      />

      <!-- 性格设定 -->
      <CustomRichTextCell
        :title="$t('User.mate.basicInfo.personality')"
        :module-name="$t('User.mate.basicInfo.title')"
        :content="mate.personalityPrompt"
        :cb="(content:string) => richEditorCallback(content, 'personalityPrompt')"
      />
    </cell-group>
    <!-- <VanCellGroup inset class="mx-4 my-2">
      <VanCell>
        <template #title>
          {{ $t("User.mate.chat.personality") }}
        </template>
        <template #label>
          <GenerableTextArea
            :label="$t('User.mate.chat.personality')"
            :mateContext="mate"
            v-model="mate.personalityPrompt"
            :promptType="MateCardPromptType.Personality"
            vertical-on-xs-screen
            @blur="emit('update:mate', 'personalityPrompt')"
          />
        </template>
      </VanCell>
    </VanCellGroup> -->
    <div class="p-4 bg-base-200 m-4 rounded-lg flex flex-col gap-2">
      <div class="flex justify-between">
        <span>{{ $t("User.mate.basicInfo.tag") }}</span>
        <span class="text-base-content/50">
          {{ $t("User.mate.basicInfo.rules.tag") }}
        </span>
      </div>
      <TagSelector :tag="mate.tag?.split(',') || []" @update:tag="updateTag" />
    </div>
    <cell-group inset>
      <slot name="inside"></slot>
    </cell-group>
    <slot name="outside"></slot>
    <popup v-model:show="showMateTypePicker" position="bottom">
      <picker
        :columns="MateTypeOptions"
        :v-model="mate.type"
        @cancel="showMateTypePicker = false"
        @confirm="onTypePickerConfirm"
      />
    </popup>
    <popup v-model:show="showGenderPicker" position="bottom">
      <picker
        :columns="GenderOptions"
        :v-model="mate.gender"
        @cancel="showGenderPicker = false"
        @confirm="onGenderPickerConfirm"
      />
    </popup>

    <media-select-popup
      v-model:show="showAvatarUploadPopup"
      :allow-crop-outside="false"
      :enable-crop="true"
      :crop-aspect-ratio="isSelfSetting ? 1 : 3 / 4"
      :max-count="1"
      :multiple="false"
      :upload-type="0"
      media-type="img"
      replace
      enable-mate-wish
      enable-draw
      @on-media-selected="onAvatarSelected"
    />

    <ModalMBTISelect
      v-model:show="showMBTIModal"
      :mbti="mate.mbti"
      @select="handleMBTISelect"
    />

    <ModalWorldViewPicker
      v-model:show="showWorldViewPicker"
      :worldview-id="
        Number(
          worldviewType === 'bornPlanet'
            ? mate.worldviewId
            : mate.currentWorldviewId
        )
      "
      @confirm="onWorldViewConfirm"
      :worldview-list="worldviewStore.worldviewList"
    />
  </div>
</template>
